import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Play, Pause, X, Square, Loader2, GripVertical, MessageCircle, Reply } from "lucide-react";
import axios from "axios";
import { useDraggable } from "../hooks/useDraggable";
import { playNotificationSound, shouldAutoplayVoice, shouldOpenPTT } from "../utils/notificationSound";
import { dayLabel, sameDay } from "../lib/dateDivider";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const VOICE_TYPES = new Set(["friend_voice", "friend_talk"]);
// Touch devices use tap-to-toggle (hold-to-talk is unreliable on mobile web).
const PTT_COARSE = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(pointer: coarse)").matches : false;
const REACT_EMOJIS = ["❤️", "😂", "👍", "😮", "😢", "🎉"];

function shortName(n) {
  if (!n) return "Friend";
  return n.length > 14 ? n.slice(0, 13) + "…" : n;
}

/**
 * A single back-and-forth voice conversation (walkie-talkie style, like the
 * game-room voice lane): play their notes AND yours in one thread, new incoming
 * notes auto-play as they arrive, and hold-to-talk records a reply that appends
 * to the same thread — instead of a new card popping up for every message.
 */
function VoiceThread({ uid, friendId, friendName }) {
  const [messages, setMessages] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [friendRecording, setFriendRecording] = useState(false);
  const [reactingId, setReactingId] = useState(null);
  const [replyTo, setReplyTo] = useState(null); // {id, fromName, label}
  const [showJump, setShowJump] = useState(false);
  const audioRef = useRef(null);
  const recRef = useRef({ mediaRecorder: null, chunks: [], stream: null, timer: null, startedAt: 0, ping: null });
  const autoPlayedRef = useRef(new Set());
  const armedRef = useRef(false); // true after first user gesture (unlocks autoplay)
  const scrollRef = useRef(null);
  const keepScrollRef = useRef(false);
  const pagedRef = useRef(false); // true once the user has loaded earlier history

  const mergeById = (a, b) => {
    const map = new Map();
    [...a, ...b].forEach((m) => map.set(m.id, m));
    return [...map.values()].sort((x, y) => (x.created_at || "").localeCompare(y.created_at || ""));
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/friends/${uid}/messages/${friendId}`, { params: { limit: 30 } });
      const voice = (r.data.messages || []).filter((m) => m.kind === "voice");
      // Merge so the live poll ADDS new notes without dropping earlier history
      // the user paged in via "Load earlier".
      setMessages((prev) => (prev.length ? mergeById(prev, voice) : voice));
      if (!pagedRef.current) setHasMore(!!r.data.has_more);
    } catch { /* silent */ }
  }, [uid, friendId]);

  // Page further back through the FULL voice history (older notes above).
  const loadEarlier = useCallback(async () => {
    if (!messages.length || loadingMore) return;
    setLoadingMore(true);
    keepScrollRef.current = true;
    pagedRef.current = true;
    const before = messages[0].created_at;
    try {
      const r = await axios.get(`${API}/friends/${uid}/messages/${friendId}`, { params: { limit: 20, before } });
      const older = (r.data.messages || []).filter((m) => m.kind === "voice");
      setMessages((prev) => mergeById(older, prev));
      setHasMore(!!r.data.has_more);
    } catch { /* silent */ }
    setLoadingMore(false);
  }, [uid, friendId, messages, loadingMore]);

  // Poll the conversation fast (3s) so the back-and-forth feels live.
  useEffect(() => {
    setMessages([]);
    setReplyTo(null);
    pagedRef.current = false;
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  // Live "friend is recording…" indicator (1.5s poll).
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/friends/${uid}/talk-status/${friendId}`);
        if (alive) setFriendRecording(!!r.data.recording);
      } catch { /* silent */ }
    };
    check();
    const id = setInterval(check, 1500);
    return () => { alive = false; clearInterval(id); };
  }, [uid, friendId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (keepScrollRef.current) { keepScrollRef.current = false; return; } // keep view when prepending history
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (friendRecording && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [friendRecording]);

  const stopPlay = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } }
    setPlayingId(null);
  };

  // Toggle an emoji reaction on a voice note (walkie-talkie thread).
  const react = async (m, emoji) => {
    setReactingId(null);
    setMessages((prev) => prev.map((x) => {
      if (x.id !== m.id) return x;
      const rx = { ...(x.reactions || {}) };
      const users = (rx[emoji] || []).filter((u) => u !== uid);
      if (!(rx[emoji] || []).includes(uid)) users.push(uid);
      if (users.length) rx[emoji] = users; else delete rx[emoji];
      return { ...x, reactions: rx };
    }));
    try {
      const r = await axios.post(`${API}/friends/message/${m.id}/react`, { user_id: uid, emoji });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: r.data.reactions || {} } : x)));
    } catch { /* poll reconciles */ }
  };

  const onThreadScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 90);
  };
  const jumpNewest = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    setShowJump(false);
  };

  const play = useCallback(async (m) => {
    armedRef.current = true;
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } }
    const a = new Audio(`${API}/friends/voice/${m.id}`);
    audioRef.current = a;
    a.onended = () => setPlayingId(null);
    a.onerror = () => setPlayingId(null);
    setPlayingId(m.id);
    try { await a.play(); } catch { setPlayingId(null); return; }
    if (m.from_user_id === friendId && !m.listened) {
      axios.post(`${API}/friends/voice/${m.id}/listened`, { user_id: uid }).catch(() => {});
    }
  }, [uid, friendId]);

  // Auto-play a freshly-arrived incoming note — only when the user has enabled
  // voice autoplay (default off for school), and always chime on arrival.
  useEffect(() => {
    const incoming = messages.filter((m) => m.from_user_id === friendId);
    const last = incoming[incoming.length - 1];
    if (!last || autoPlayedRef.current.has(last.id)) return;
    autoPlayedRef.current.add(last.id);
    const fresh = Date.now() - new Date(last.created_at).getTime() < 25000;
    if (!fresh) return;
    try { playNotificationSound(friendId); } catch { /* sound optional */ } // friend's sound (or default) on a new voice message
    if (armedRef.current && !playingId && shouldAutoplayVoice()) play(last);
  }, [messages, friendId, play, playingId]);

  const getMic = async () => {
    const s = recRef.current.stream;
    if (s && s.getAudioTracks().some((t) => t.readyState === "live")) return s;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recRef.current.stream = stream;
    return stream;
  };

  const uploadReply = async () => {
    const chunks = recRef.current.chunks;
    const dur = Math.round((Date.now() - recRef.current.startedAt) / 100) / 10;
    if (!chunks.length || dur < 0.3) return;
    const blob = new Blob(chunks, { type: (recRef.current.mediaRecorder && recRef.current.mediaRecorder.mimeType) || "audio/mp4" });
    const bt = (blob.type || "").toLowerCase();
    const ext = bt.includes("mp4") || bt.includes("m4a") || bt.includes("aac") ? "m4a" : bt.includes("webm") ? "webm" : bt.includes("ogg") ? "ogg" : "m4a";
    const fd = new FormData();
    fd.append("audio", blob, `voice.${ext}`);
    fd.append("from_user_id", uid);
    fd.append("to_user_id", friendId);
    fd.append("duration", String(dur));
    if (replyTo?.id) fd.append("reply_to", replyTo.id);
    setUploading(true);
    try {
      await axios.post(`${API}/friends/voice-message`, fd);
      setReplyTo(null);
      await load();
    } catch { /* silent */ }
    setUploading(false);
    releaseMic(); // close the mic right after sending so the indicator turns off
  };

  const stopRec = () => {
    const mr = recRef.current.mediaRecorder;
    if (recRef.current.timer) { clearInterval(recRef.current.timer); recRef.current.timer = null; }
    if (recRef.current.ping) { clearInterval(recRef.current.ping); recRef.current.ping = null; }
    // If a stop lands WHILE startRec is still awaiting the mic, flag it so the
    // async startRec aborts cleanly instead of leaving a stuck recorder + ping.
    recRef.current.stopWanted = true;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
    // Clear the "is recording" indicator on the friend's side immediately.
    if (uid && friendId) axios.post(`${API}/friends/talk-signal/clear`, { from_user_id: uid, to_user_id: friendId }).catch(() => {});
  };

  const startRec = async () => {
    armedRef.current = true;
    stopPlay();
    recRef.current.stopWanted = false;
    try {
      const stream = await getMic();
      // Released (or cancelled) before the mic was ready — abort, don't start.
      if (recRef.current.stopWanted) { setRecording(false); releaseMic(); return; }
      const cands = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
      const mime = cands.find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } }) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recRef.current.mediaRecorder = mr;
      recRef.current.chunks = [];
      recRef.current.startedAt = Date.now();
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recRef.current.chunks.push(e.data); };
      mr.onstop = () => uploadReply();
      mr.start();
      setRecording(true);
      setSeconds(0);
      // Tell the friend we're recording (live pulse) — ping now + every 2s.
      const ping = () => axios.post(`${API}/friends/talk-signal`, { from_user_id: uid, to_user_id: friendId }).catch(() => {});
      ping();
      recRef.current.ping = setInterval(ping, 2000);
      recRef.current.timer = setInterval(() => setSeconds((s) => {
        if (s >= 30) { stopRec(); return 30; }
        return s + 1;
      }), 1000);
    } catch {
      setRecording(false);
    }
  };

  const releaseMic = () => {
    const s = recRef.current.stream;
    if (s) s.getTracks().forEach((t) => t.stop());
    recRef.current.stream = null;
  };
  useEffect(() => () => { stopPlay(); if (recRef.current.timer) clearInterval(recRef.current.timer); if (recRef.current.ping) clearInterval(recRef.current.ping); releaseMic(); }, []);

  // Safety net: if the tab is backgrounded or loses focus mid-hold, stop the
  // recording so a leaked ping can't leave the friend stuck on "is recording".
  useEffect(() => {
    const stopIfRecording = () => { if (recRef.current.ping || recRef.current.mediaRecorder?.state === "recording") stopRec(); };
    const onVis = () => { if (document.hidden) stopIfRecording(); };
    window.addEventListener("blur", stopIfRecording);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("blur", stopIfRecording); document.removeEventListener("visibilitychange", onVis); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-[300px] max-w-[86vw]">
      <div ref={scrollRef} onScroll={onThreadScroll} className="max-h-[min(55vh,420px)] overflow-y-auto px-3 py-2 space-y-2 relative" data-testid="voice-thread">
        {hasMore && messages.length > 0 && (
          <button onClick={loadEarlier} disabled={loadingMore} data-testid="voice-thread-load-earlier"
            className="w-full text-[10px] font-bold text-violet-200/70 hover:text-white py-1.5 rounded-lg bg-white/[0.04] border border-white/8 mb-1">
            {loadingMore ? "Loading…" : "↑ Load earlier voice notes"}
          </button>
        )}
        {messages.length === 0 ? (
          <p className="text-white/40 text-xs text-center py-4">Loading your voice chat…</p>
        ) : messages.map((m, i) => {
          const mine = m.from_user_id === uid;
          const isPlaying = playingId === m.id;
          const hasReactions = m.reactions && Object.keys(m.reactions).length > 0;
          const showDateDivider = i === 0 || !sameDay(m.created_at, messages[i - 1]?.created_at);
          return (
            <React.Fragment key={m.id}>
            {showDateDivider && m.created_at && (
              <div className="flex items-center gap-2 py-1" data-testid={`voice-date-divider-${i}`}>
                <span className="flex-1 h-px bg-white/10" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-white/40 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">{dayLabel(m.created_at)}</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
            )}
            <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`voice-msg-${m.id}`}>
              {m.reply_to && (
                <div className={`mb-0.5 pl-2 border-l-2 rounded-sm text-[10px] max-w-[200px] ${mine ? "border-cyan-300/50 text-right self-end pr-2 border-l-0 border-r-2" : "border-violet-300/50"}`} data-testid={`voice-reply-quote-${m.id}`}>
                  <span className="font-bold text-white/50">{m.reply_from_name || "Reply"}</span>
                  <span className="block text-white/40 truncate">{m.reply_kind === "voice" ? "🎤 " : ""}{m.reply_snippet}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                {mine && (
                  <button onClick={() => setReactingId(reactingId === m.id ? null : m.id)} data-testid={`voice-react-btn-${m.id}`}
                    className="text-white/25 hover:text-white/70 text-xs leading-none px-1">☺</button>
                )}
                <button
                  onClick={() => (isPlaying ? stopPlay() : play(m))}
                  data-testid={`voice-play-${m.id}`}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 max-w-[200px] ${mine ? "bg-violet-500/25 border border-violet-400/40" : "bg-white/8 border border-white/12"}`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center ${mine ? "bg-violet-500" : "bg-white/15"}`}>
                    {isPlaying ? <Pause size={13} className="text-white" /> : <Play size={13} className="text-white" />}
                  </span>
                  <span className="flex items-center gap-[2px] h-4">
                    {[6, 11, 7, 13, 8, 5].map((h, i) => (
                      <span key={i} className={`w-[2px] rounded-full ${mine ? "bg-violet-200/80" : "bg-white/50"}`}
                        style={{ height: h, animation: isPlaying ? `iw-eq .8s ${i * 0.08}s infinite ease-in-out` : "none" }} />
                    ))}
                  </span>
                  <span className="text-[10px] text-white/60">{m.duration ? `${m.duration}s` : ""}</span>
                  {mine && m.listened && <span className="text-[9px] text-violet-200/80 font-bold">✓</span>}
                </button>
                {!mine && (
                  <button onClick={() => setReactingId(reactingId === m.id ? null : m.id)} data-testid={`voice-react-btn-${m.id}`}
                    className="text-white/25 hover:text-white/70 text-xs leading-none px-1">☺</button>
                )}
                <button
                  onClick={() => setReplyTo({ id: m.id, fromName: mine ? "You" : shortName(friendName), label: `🎤 Voice${m.duration ? ` · ${m.duration}s` : ""}` })}
                  data-testid={`voice-reply-btn-${m.id}`} title="Reply to this voice note"
                  className="text-white/25 hover:text-violet-300 transition-colors px-1"><Reply size={12} /></button>
              </div>
              {m.transcript ? (
                <span className={`text-[10px] text-white/45 italic mt-0.5 max-w-[210px] ${mine ? "text-right" : "text-left"}`} data-testid={`voice-transcript-${m.id}`}>
                  “{m.transcript}”
                </span>
              ) : null}
              {reactingId === m.id && (
                <div className="flex items-center gap-0.5 bg-slate-800 border border-white/15 rounded-full px-1.5 py-0.5 shadow-lg mt-1" data-testid={`voice-react-picker-${m.id}`}>
                  {REACT_EMOJIS.map((e) => (
                    <button key={e} onClick={() => react(m, e)} className="text-sm hover:scale-125 transition-transform" data-testid={`voice-react-${e}-${m.id}`}>{e}</button>
                  ))}
                </div>
              )}
              {hasReactions && (
                <div className={`flex items-center gap-1 mt-1 ${mine ? "flex-row-reverse" : ""}`} data-testid={`voice-reactions-${m.id}`}>
                  {Object.entries(m.reactions).map(([e, users]) => (
                    <button key={e} onClick={() => react(m, e)}
                      className={`text-[11px] leading-none rounded-full px-1.5 py-0.5 border ${(users || []).includes(uid) ? "bg-violet-500/30 border-violet-400/40" : "bg-white/5 border-white/10"}`}>
                      {e} {(users || []).length}
                    </button>
                  ))}
                </div>
              )}
            </div>
            </React.Fragment>
          );
        })}
        {friendRecording && (
          <div className="flex justify-start" data-testid="voice-thread-recording">
            <div className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-white/8 border border-violet-400/30">
              <Mic size={13} className="text-violet-300 animate-pulse" />
              <span className="flex items-center gap-[2px] h-4">
                {[6, 11, 7, 13, 8].map((h, i) => (
                  <span key={i} className="w-[2px] rounded-full bg-violet-300"
                    style={{ height: h, animation: `iw-eq .7s ${i * 0.09}s infinite ease-in-out` }} />
                ))}
              </span>
              <span className="text-[10px] text-violet-200/80 font-bold">{shortName(friendName)} is recording…</span>
            </div>
          </div>
        )}
      </div>
      {showJump && (
        <div className="flex justify-center -mt-1">
          <button onClick={jumpNewest} data-testid="voice-thread-jump-newest"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500 hover:bg-violet-400 text-white text-[10px] font-bold shadow-lg">
            ↓ Newest
          </button>
        </div>
      )}
      <div className="px-3 pb-3 pt-1 border-t border-white/8">
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 rounded-lg bg-violet-500/10 border border-violet-400/25 px-2.5 py-1.5" data-testid="voice-reply-bar">
            <Reply size={12} className="text-violet-300 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-violet-200 text-[10px] font-bold leading-none">Replying to {replyTo.fromName}</p>
              <p className="text-white/45 text-[10px] truncate">{replyTo.label}</p>
            </div>
            <button onClick={() => setReplyTo(null)} data-testid="voice-reply-cancel" className="text-white/40 hover:text-white flex-shrink-0"><X size={13} /></button>
          </div>
        )}
        <button
          {...(PTT_COARSE
            ? { onClick: () => { if (uploading) return; if (recording) stopRec(); else startRec(); } }
            : { onMouseDown: startRec, onMouseUp: stopRec, onMouseLeave: () => recording && stopRec() })}
          disabled={uploading}
          data-testid="voice-thread-record"
          className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 font-bold text-sm transition-all select-none ${recording ? "bg-red-500 text-white scale-[1.02]" : "bg-violet-500 hover:bg-violet-400 text-white"}`}
          style={{ touchAction: "manipulation" }}
        >
          {uploading ? <><Loader2 size={15} className="animate-spin" /> Sending…</>
            : recording ? <><Square size={14} /> {PTT_COARSE ? "Tap to send" : "Release to send"} · {seconds}s</>
            : <><Mic size={15} /> {PTT_COARSE ? "Tap to talk" : "Hold to talk"}</>}
        </button>
      </div>
    </div>
  );
}

export default function IncomingVoicePopup({ user }) {
  const navigate = useNavigate();
  const [senders, setSenders] = useState([]); // [{id, name, notifIds:[], ts}]
  const [manual, setManual] = useState([]);   // user-opened threads [{id, name, ts}]
  const [activeId, setActiveId] = useState(null);
  const [, setSettingsTick] = useState(0); // bump to re-resolve PTT (global + per-friend)
  const dismissed = useRef(new Set());

  // React live to the Notifications-tab toggle + per-friend overrides (no reload).
  useEffect(() => {
    const bump = () => setSettingsTick((t) => t + 1);
    window.addEventListener("iw:notif-settings", bump);
    window.addEventListener("iw:friend-ptt", bump);
    return () => { window.removeEventListener("iw:notif-settings", bump); window.removeEventListener("iw:friend-ptt", bump); };
  }, []);

  // User tapped "Talk" (Friends rail / profile) → open ONE shared walkie-talkie
  // thread for that friend here (carries full history), instead of a separate
  // widget. Incoming voice notes land in this SAME popup, so a conversation is
  // never split across two cards.
  useEffect(() => {
    const onTalk = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      dismissed.current.delete(id);
      setManual((prev) => (prev.some((m) => m.id === id) ? prev : [...prev, { id, name: e.detail?.name || "Friend", ts: new Date().toISOString() }]));
      setActiveId(id);
    };
    window.addEventListener("iw:friend-talk", onTalk);
    return () => window.removeEventListener("iw:friend-talk", onTalk);
  }, []);

  const uid = user && !user.isDemo ? (user.id || user.email) : null;
  const drag = useDraggable(!!activeId, { width: 300, height: 320, bottomGap: 88 });

  const markRead = useCallback((notifIds) => {
    if (!uid) return;
    (notifIds || []).forEach((id) => axios.post(`${API}/notifications/user/${uid}/read/${id}`).catch(() => {}));
  }, [uid]);

  const poll = useCallback(async () => {
    if (!uid) return;
    try {
      const r = await axios.get(`${API}/notifications/user/${uid}?limit=25`);
      const groups = new Map();
      for (const n of (r.data.notifications || [])) {
        if (n.read || !VOICE_TYPES.has(n.type) || !n.from_user_id) continue;
        if (dismissed.current.has(n.from_user_id)) continue;
        const g = groups.get(n.from_user_id) || { id: n.from_user_id, name: n.from_name || "Friend", notifIds: [], ts: n.created_at };
        g.notifIds.push(n.id);
        if ((n.created_at || "") > (g.ts || "")) g.ts = n.created_at;
        groups.set(n.from_user_id, g);
      }
      const list = [...groups.values()].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
      setSenders(list);
      setActiveId((cur) => cur || (list[0]?.id || null));
    } catch { /* silent */ }
  }, [uid]);

  useEffect(() => {
    if (!uid) { setSenders([]); setManual([]); setActiveId(null); dismissed.current = new Set(); return; }
    poll();
    const id = setInterval(poll, 3000);
    // Instant: a pushed voice note pops the walkie-talkie popup immediately.
    const onEvent = () => poll();
    window.addEventListener("iw:social-event", onEvent);
    return () => { clearInterval(id); window.removeEventListener("iw:social-event", onEvent); };
  }, [uid, poll]);

  // The popup stays on screen until the user closes it (X) or opens the full chat;
  // do NOT auto-mark-read on display, or the next poll would drop it and the
  // popup would vanish on its own. The bell keeps reflecting the unread message
  // until X / Open full chat (both call markRead).

  // Merge notif-driven senders with user-opened (manual) threads. Manual threads
  // are always visible (the user explicitly tapped "Talk"); notif senders respect
  // the per-friend push-to-talk setting. One card per friend — never stacked.
  const byId = new Map();
  for (const s of senders) byId.set(s.id, { ...s });
  for (const m of manual) {
    const ex = byId.get(m.id);
    if (ex) { ex.manual = true; if (!ex.name || ex.name === "Friend") ex.name = m.name; }
    else byId.set(m.id, { id: m.id, name: m.name, notifIds: [], ts: m.ts, manual: true });
  }
  // Only ever show threads the user explicitly opened (tapped "Talk", or tapped
  // a voice notification in the bell → iw:friend-talk). We NEVER auto-open the
  // popup from unread voice notifications on load/refresh — those live in the
  // bell until the user taps them. This keeps push-to-talk from appearing on
  // its own or carrying over from a previous session.
  const visible = [...byId.values()]
    .filter((s) => s.manual)
    .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  const active = visible.find((s) => s.id === activeId) || visible[0];
  if (!active) return null;

  const close = () => {
    dismissed.current.add(active.id);
    markRead(active.notifIds);
    setManual((prev) => prev.filter((s) => s.id !== active.id));
    const rest = visible.filter((s) => s.id !== active.id);
    setSenders((prev) => prev.filter((s) => s.id !== active.id));
    setActiveId(rest[0]?.id || null);
  };

  const posStyle = drag.pos ? { left: drag.pos.x, top: drag.pos.y } : { right: 16, bottom: 88 };

  return (
    <>
      <style>{`@keyframes iw-eq{0%,100%{height:30%}50%{height:100%}}`}</style>
      <div data-testid="incoming-voice-popup" className="fixed z-[9999] pointer-events-none" style={posStyle}>
        <div className="pointer-events-auto w-[300px] max-w-[86vw] rounded-2xl border-2 border-violet-500/40 bg-slate-900 shadow-2xl overflow-hidden" data-testid="voice-conversation">
          {/* Header = drag handle */}
          <div className="flex items-center justify-between px-3 py-2 bg-violet-500/15 select-none" {...drag.gripHandlers} title="Drag to move">
            <div className="flex items-center gap-2 min-w-0">
              <GripVertical size={13} className="text-white/40 shrink-0" />
              <Mic size={14} className="text-violet-300 shrink-0" />
              <span className="text-white text-sm font-bold truncate">Talk with {shortName(active.name)}</span>
            </div>
            <button onClick={close} data-testid="voice-popup-close" className="w-6 h-6 rounded-full bg-white/8 text-white/60 hover:text-white flex items-center justify-center shrink-0"><X size={13} /></button>
          </div>

          {/* Other waiting conversations — switch instead of stacking cards */}
          {visible.length > 1 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.03] overflow-x-auto">
              {visible.map((s) => (
                <button key={s.id} onClick={() => setActiveId(s.id)} data-testid={`voice-switch-${s.id}`}
                  className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap shrink-0 ${s.id === activeId ? "bg-violet-500 text-white" : "bg-white/8 text-white/60 hover:bg-white/15"}`}>
                  {shortName(s.name)}{s.id !== activeId ? ` · ${s.notifIds.length}` : ""}
                </button>
              ))}
            </div>
          )}

          <VoiceThread uid={uid} friendId={active.id} friendName={active.name} />

          <button onClick={() => { markRead(active.notifIds); navigate(`/friends?chat=${active.id}`); close(); }}
            data-testid="voice-popup-openchat"
            className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold text-violet-200/80 hover:text-white bg-white/[0.03] border-t border-white/8">
            <MessageCircle size={12} /> Open full chat
          </button>
        </div>
      </div>
    </>
  );
}
